import { BoxProps, ButtonProps } from '@chakra-ui/react';
import { useState } from 'react';
import { handleApiErrorToast } from '../../api/axios';
import { useGenerateCustomerPortalShareLink } from '../../api/customerPortalService';
import { useNavigatorPermission } from '../../hooks/useNavigatorPermission';
import { copyToClipboard } from '../../utils';
import { MBox, MButton, MText } from '../Monetize';

export interface CustomerPortalShareLinkCopyButtonProps {
  accountId: string;
  billGroupId: string;
  beforeCopyText?: string;
  afterCopyText?: string;
  buttonProps?: ButtonProps;
  /** If copy to clipboard is not available or fails, link is shows as text */
  showLinkProps?: BoxProps;
}

export function CustomerPortalShareLinkCopyButton({
  accountId,
  billGroupId,
  beforeCopyText = 'Copy Portal Link',
  afterCopyText = 'Copied',
  buttonProps,
  showLinkProps,
}: CustomerPortalShareLinkCopyButtonProps) {
  const { canCopyClipboard } = useNavigatorPermission();
  const { mutateAsync: generateLink, isPending } =
    useGenerateCustomerPortalShareLink({ meta: { showErrorToast: true } });

  const [isCopied, setIsCopied] = useState(false);
  const [showValue, setShowValue] = useState(false);
  const [value, setValue] = useState('');

  const handleClick = async (ev: React.MouseEvent<HTMLElement>) => {
    ev.stopPropagation();
    try {
      const res = await generateLink({ accountId, billGroupId });
      setValue(res.url);

      if (!canCopyClipboard) {
        setShowValue(true);
        return;
      }

      const didCopy = await copyToClipboard(res.url);
      if (!didCopy) {
        setShowValue(true);
        return;
      }

      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  if (showValue && value) {
    return (
      <MBox {...showLinkProps}>
        <MText>{value}</MText>
      </MBox>
    );
  }

  return (
    <MButton
      variant="tertiary"
      size="sm"
      onClick={handleClick}
      fontStyle={isCopied ? 'italic' : undefined}
      minW="auto"
      isLoading={isPending}
      disabled={isPending}
      {...buttonProps}
    >
      {isCopied ? afterCopyText : beforeCopyText}
    </MButton>
  );
}
